import { useQueryClient } from '@tanstack/react-query';
import useGetApi from '../useGetApi';
import useMutationApi from '../useMutationApi';
import {
  MonthlyTrendingComparativeViewValues,
  type DivisionRebateSummaryResponse,
  type SupplierTagSummaryResponse,
  type DailyEarningsPYEResponse,
  type MonthlyTrendingComparativeView,
  type MonthlyTrendingResponse,
  type SupplierPaymentTermsResponse,
  type ProfitabilityReportResponse,
  type SupplierSalesMarginUploadResponse,
  type SupplierSalesMarginValidateResponse,
  type SupplierYoYResponse,
  type ReportLineType,
} from '@erp/shared-types';
import type { UseQueryResult } from '@tanstack/react-query';
import type ApiError from '../ApiError';

interface DivisionRebateSummaryParams {
  year: number;
  divisionIds?: string[];
  lineType: ReportLineType;
  categoryFilters?: string[];
  enabled?: boolean;
}

/**
 * Hook to fetch division rebate summary report
 */
export const useDivisionRebateSummary = (
  params: DivisionRebateSummaryParams,
): UseQueryResult<DivisionRebateSummaryResponse | undefined, ApiError> => {
  return useGetApi<
    DivisionRebateSummaryResponse,
    DivisionRebateSummaryResponse
  >({
    endpoint: 'rebates/reports/division-summary',
    queryParameters: {
      year: params.year.toString(),
      ...(params.divisionIds &&
        params.divisionIds.length > 0 && {
          divisionIds: params.divisionIds.join(','),
        }),
      lineType: params.lineType,
      ...(params.categoryFilters &&
        params.categoryFilters.length > 0 && {
          categoryFilters: params.categoryFilters.join(','),
        }),
    },
    options: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): DivisionRebateSummaryResponse => {
      if (!data) {
        // Return a default empty response instead of null/undefined
        return {
          year: new Date().getFullYear(),
          divisionNames: [],
          lineType: 'reported',
          generatedAt: new Date(),
          ruleTagTable: {
            title: '',
            columnHeaders: [],
            rows: [],
          },
          supplierTable: {
            title: '',
            columnHeaders: [],
            rows: [],
          },
        };
      }

      // Parse generatedAt date
      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface SupplierTagSummaryParams {
  year: number;
  divisionIds?: string[];
  supplierTagIds?: string[];
  lineType: ReportLineType;
  enabled?: boolean;
}

/**
 * Hook to fetch supplier tag summary report
 */
export const useSupplierTagSummary = (
  params: SupplierTagSummaryParams,
): UseQueryResult<SupplierTagSummaryResponse | undefined, ApiError> => {
  return useGetApi<SupplierTagSummaryResponse, SupplierTagSummaryResponse>({
    endpoint: 'rebates/reports/supplier-tag-summary',
    queryParameters: {
      year: params.year.toString(),
      ...(params.divisionIds &&
        params.divisionIds.length > 0 && {
          divisionIds: params.divisionIds.join(','),
        }),
      ...(params.supplierTagIds &&
        params.supplierTagIds.length > 0 && {
          supplierTagIds: params.supplierTagIds.join(','),
        }),
      lineType: params.lineType,
    },
    options: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): SupplierTagSummaryResponse => {
      if (!data) {
        // Return a default empty response instead of null/undefined
        return {
          year: new Date().getFullYear(),
          divisionNames: [],
          supplierTagNames: [],
          lineType: 'reported',
          generatedAt: new Date(),
          supplierTagTable: {
            title: '',
            columnHeaders: [],
            rows: [],
          },
        };
      }

      // Parse generatedAt date
      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface DailyEarningsPYEParams {
  year: number;
  divisionIds?: string[];
  categoryFilters?: string[];
  priorYearLineType?: 'reported' | 'estimates' | 'predictions';
  throughMonth?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch daily earnings PYE report
 */
export const useDailyEarningsPYE = (
  params: DailyEarningsPYEParams,
): UseQueryResult<DailyEarningsPYEResponse | undefined, ApiError> => {
  return useGetApi<DailyEarningsPYEResponse, DailyEarningsPYEResponse>({
    endpoint: 'rebates/reports/daily-earnings-pye',
    queryParameters: {
      year: params.year.toString(),
      ...(params.divisionIds &&
        params.divisionIds.length > 0 && {
          divisionIds: params.divisionIds.join(','),
        }),
      ...(params.categoryFilters &&
        params.categoryFilters.length > 0 && {
          categoryFilters: params.categoryFilters.join(','),
        }),
      ...(params.priorYearLineType && {
        priorYearLineType: params.priorYearLineType,
      }),
      ...(params.throughMonth !== undefined && {
        throughMonth: params.throughMonth.toString(),
      }),
    },
    options: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): DailyEarningsPYEResponse => {
      if (!data) {
        // Return a default empty response instead of null/undefined
        return {
          year: new Date().getFullYear() - 1,
          priorYear: new Date().getFullYear() - 2,
          divisionNames: [],
          generatedAt: new Date(),
          table: {
            title: '',
            columnHeaders: [],
            rows: [],
          },
          monthlyBreakdownTable: {
            title: '',
            columnHeaders: [],
            rows: [],
          },
          lastSnapshotDate: null,
        };
      }

      // Parse generatedAt date
      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface MonthlyTrendingParams {
  year: number;
  divisionIds?: string[];
  priorYearLineType?: 'reported' | 'estimates' | 'predictions';
  throughMonth?: number;
  comparativeView?: MonthlyTrendingComparativeView;
  enabled?: boolean;
}

/**
 * Hook to fetch monthly trending report
 */
export const useMonthlyTrending = (
  params: MonthlyTrendingParams,
): UseQueryResult<MonthlyTrendingResponse | undefined, ApiError> => {
  return useGetApi<MonthlyTrendingResponse, MonthlyTrendingResponse>({
    endpoint: 'rebates/reports/monthly-trending',
    queryParameters: {
      year: params.year.toString(),
      ...(params.divisionIds &&
        params.divisionIds.length > 0 && {
          divisionIds: params.divisionIds.join(','),
        }),
      ...(params.priorYearLineType && {
        priorYearLineType: params.priorYearLineType,
      }),
      ...(params.throughMonth !== undefined && {
        throughMonth: params.throughMonth.toString(),
      }),
      ...(params.comparativeView ===
        MonthlyTrendingComparativeViewValues.REPORTED && {
        comparativeView: params.comparativeView,
      }),
    },
    options: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): MonthlyTrendingResponse => {
      if (!data) {
        const emptyTable = { title: '', columnHeaders: [], rows: [] };
        // Return a default empty response instead of null/undefined
        return {
          year: new Date().getFullYear(),
          priorYear: new Date().getFullYear() - 1,
          divisionNames: [],
          generatedAt: new Date(),
          table: emptyTable,
          monthlyBreakdownTables: {
            total: emptyTable,
            redistribution: emptyTable,
            conditional: emptyTable,
            nonConditional: emptyTable,
          },
        };
      }

      // Parse generatedAt date
      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface SupplierYoYParams {
  year: number;
  divisionIds?: string[];
  thisYearLineType: ReportLineType;
  lastYearLineType: ReportLineType;
  categoryFilters?: string[];
  throughMonth?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch supplier year-over-year comparison report
 */
export const useSupplierYoY = (
  params: SupplierYoYParams,
): UseQueryResult<SupplierYoYResponse | undefined, ApiError> => {
  return useGetApi<SupplierYoYResponse, SupplierYoYResponse>({
    endpoint: 'rebates/reports/supplier-yoy',
    queryParameters: {
      year: params.year.toString(),
      ...(params.divisionIds &&
        params.divisionIds.length > 0 && {
          divisionIds: params.divisionIds.join(','),
        }),
      thisYearLineType: params.thisYearLineType,
      lastYearLineType: params.lastYearLineType,
      ...(params.categoryFilters &&
        params.categoryFilters.length > 0 && {
          categoryFilters: params.categoryFilters.join(','),
        }),
      ...(params.throughMonth !== undefined && {
        throughMonth: params.throughMonth.toString(),
      }),
    },
    options: {
      staleTime: 30000,
      gcTime: 5 * 60 * 1000,
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): SupplierYoYResponse => {
      if (!data) {
        return {
          year: new Date().getFullYear(),
          priorYear: new Date().getFullYear() - 1,
          divisionNames: [],
          thisYearLineType: 'predictions',
          lastYearLineType: 'reported',
          generatedAt: new Date(),
          table: {
            title: '',
            columnHeaders: [],
            rows: [],
          },
        };
      }

      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface SupplierPaymentTermsParams {
  year: number;
  enabled?: boolean;
}

/**
 * Hook to fetch supplier payment terms report
 */
export const useSupplierPaymentTerms = (
  params: SupplierPaymentTermsParams,
): UseQueryResult<SupplierPaymentTermsResponse | undefined, ApiError> => {
  return useGetApi<SupplierPaymentTermsResponse, SupplierPaymentTermsResponse>({
    endpoint: 'rebates/reports/supplier-payment-terms',
    queryParameters: {
      year: params.year.toString(),
    },
    options: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): SupplierPaymentTermsResponse => {
      if (!data) {
        // Return a default empty response instead of null/undefined
        return {
          year: new Date().getFullYear(),
          generatedAt: new Date(),
          data: [],
        };
      }

      // Parse generatedAt date
      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface ProfitabilityReportParams {
  year: number;
  dimensions?: [string, string, string];
  month?: number | null; // 1-12 for Jan-Dec, null for YTD
  lineType?: ReportLineType;
  divisionIds?: string[];
  supplierIds?: string[];
  productFamilyDescs?: string[];
  shipTypes?: number[]; // Filter by ship types [1, 2, 3]
  enabled?: boolean;
}

/**
 * Hook to fetch profitability report
 */
export const useProfitabilityReport = (
  params: ProfitabilityReportParams,
): UseQueryResult<ProfitabilityReportResponse | undefined, ApiError> => {
  return useGetApi<ProfitabilityReportResponse, ProfitabilityReportResponse>({
    endpoint: 'rebates/reports/profitability',
    queryParameters: {
      year: params.year.toString(),
      ...(params.dimensions && {
        dimensions: params.dimensions.join(','),
      }),
      ...(params.month !== undefined &&
        params.month !== null && { month: params.month.toString() }),
      ...(params.lineType && { lineType: params.lineType }),
      ...(params.divisionIds &&
        params.divisionIds.length > 0 && {
          divisionIds: params.divisionIds.join(','),
        }),
      ...(params.supplierIds &&
        params.supplierIds.length > 0 && {
          supplierIds: params.supplierIds.join(','),
        }),
      ...(params.productFamilyDescs &&
        params.productFamilyDescs.length > 0 && {
          productFamilyDescs: params.productFamilyDescs.join('|'),
        }),
      ...(params.shipTypes &&
        params.shipTypes.length > 0 && {
          shipTypes: params.shipTypes.map(String).join(','),
        }),
    },
    options: {
      staleTime: 30000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      enabled: params.enabled ?? true,
    },
    transformResponse: (data): ProfitabilityReportResponse => {
      if (!data) {
        return {
          year: new Date().getFullYear(),
          generatedAt: new Date(),
          rows: [],
        };
      }

      // Parse generatedAt date
      if (data.generatedAt) {
        data.generatedAt = new Date(data.generatedAt);
      }

      return data;
    },
  });
};

interface ValidateProfitabilityDataRequest {
  file: File;
}

/**
 * Hook to validate profitability data (XLSX file) before uploading
 * Returns unmatched supplier codes so the user can decide whether to proceed
 */
export const useValidateProfitabilityData = () => {
  return useMutationApi<
    ValidateProfitabilityDataRequest,
    SupplierSalesMarginValidateResponse
  >({
    endpoint: 'rebates/reports/profitability/validate',
    method: 'POST',
    transformRequest: (body: ValidateProfitabilityDataRequest) => {
      const formData = new FormData();
      formData.append('file', body.file);
      return formData;
    },
    showErrorToast: false,
  });
};

interface UploadProfitabilityDataRequest {
  file: File;
  skipUnmatched?: boolean;
  year?: number;
}

/**
 * Hook to upload profitability data (XLSX file)
 * @param skipUnmatched - If true, skip rows with unmatched suppliers instead of failing
 * @param year - Year for the uploaded data (defaults to current year if not provided)
 */
export const useUploadProfitabilityData = () => {
  return useMutationApi<
    UploadProfitabilityDataRequest,
    SupplierSalesMarginUploadResponse
  >({
    endpoint: 'rebates/reports/profitability/upload',
    method: 'POST',
    transformRequest: (body: UploadProfitabilityDataRequest) => {
      const formData = new FormData();
      formData.append('file', body.file);
      if (body.skipUnmatched) {
        formData.append('skipUnmatched', 'true');
      }
      if (body.year !== undefined) {
        formData.append('year', body.year.toString());
      }
      return formData;
    },
    successMessage: 'Sales margin data uploaded successfully',
    showErrorToast: false, // We'll handle errors manually to show error table
  });
};

/**
 * Hook to fetch all unique product family descriptions from sales margins
 */
export const useProductFamilyDescriptions = (): UseQueryResult<
  string[] | undefined,
  ApiError
> => {
  return useGetApi<string[], string[]>({
    endpoint: 'rebates/product-family-descriptions',
    options: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
    transformResponse: (data): string[] => {
      if (!data || !Array.isArray(data)) {
        return [];
      }
      return data;
    },
  });
};

/**
 * Hook to refresh the PYE snapshot for a given fiscal year.
 * Triggers snapshot regeneration using current prediction data,
 * then invalidates the PYE report query so the UI refreshes.
 */
export const useRefreshPYESnapshot = () => {
  const queryClient = useQueryClient();

  return useMutationApi<
    { fiscalYear: number },
    { data: { success: boolean; snapshotDate: string; message: string } }
  >({
    endpoint: 'rebates/reports/daily-earnings-pye/refresh-snapshot',
    method: 'POST',
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['rebates/reports/daily-earnings-pye'],
        });
      },
    },
    successMessage: 'PYE snapshot refreshed',
  });
};
